/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
  styled,
  Drawer as MuiDrawer,
  ListItemIcon as MuiListItemIcon,
  ListItemText,
  MenuList,
  MenuItem as MuiMenuItem,
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction as MuiBottomNavigationAction,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useSelector } from 'react-redux';

import logo from '../../assets/images/FarmlandsProLogoWhite.svg';
import {
  BOTTOM_NAVIGATION_HEIGHT,
  DRAWER_DIMENSIONS,
  ScrollYPositionKeys,
} from '../../util/consts';
import { TestIds } from '../../testIds';
import MaterialSymbol from '../MaterialSymbol';
import { saveScrollYPosition, shouldForwardProp } from '../../util/helpers';
import onClickDeveloper from '../../util/exportAuth';
import { selectRemoteConfig } from '../../services/firebase/remote-config/remoteConfig.slice';
import { HOME_ROUTE, HOME_ROUTE_NAME } from '../../util/routing';
import { ACCOUNT_ROUTE, ACCOUNT_ROUTE_NAME } from '../../features/account/account.routes';
import { ORDERS_ROUTE, ORDERS_ROUTE_NAME } from '../../features/orders/orders.routes';
import { PRODUCTS_ROUTE, PRODUCTS_ROUTE_NAME } from '../../features/products/products.routes';
import { QUOTES_ROUTE } from '../../features/quotes/quotes.routes';
import { AGREEMENTS_ROUTE } from '../../features/sales-agreements/sales-agreements-routes';
import { SEED_ROUTE, SEED_ROUTE_NAME } from '../../features/seed-orders/seed.routes';
import { selectCurrentFarmerAccount } from '../../features/azure-b2c/storage';

const DrawerContainer = styled('div')(
  ({ theme }) => `
  margin: 24px 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
  width: auto;

  @media (max-width: ${theme.breakpoints.values.lg}px) {
    width: auto;
    margin: 24px 8px;
  }
`,
);

const BottomNavigation = styled(MuiBottomNavigation, {
  shouldForwardProp: (prop) => shouldForwardProp<{ appInstalled: boolean }>(['appInstalled'], prop),
})<{ appInstalled: boolean }>(({ theme, appInstalled }) => ({
  minHeight: `${BOTTOM_NAVIGATION_HEIGHT}px`,
  height: 'auto',
  paddingTop: '16px',
  paddingBottom: '16px',
  justifyContent: 'space-evenly',
  [theme.breakpoints.down('md')]: {
    paddingBottom: appInstalled ? '24px' : '16px',
  },
}));

const BottomNavigationAction = styled(MuiBottomNavigationAction)(() => ({
  minWidth: '0',
  padding: '0',
}));

const Logo = styled('img')(({ theme }) => ({
  margin: '40px auto',
  marginTop: '16px',
  [theme.breakpoints.between('md', 'lg')]: {
    margin: '31px auto',
    marginTop: '0px',
    maxWidth: '168px',
    width: '80%',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    margin: '24px auto',
    marginTop: '0px',
    padding: '0px 8px',
    width: 'auto',
    maxWidth: DRAWER_DIMENSIONS.width.sm,
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const LogoWrapper = styled('div')`
  display: flex;
  justify-content: center;
`;

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  transition: 'padding 0.5s ease',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const ListItemIcon = styled(MuiListItemIcon)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    marginRight: 8,
  },
  '& .material-symbols-outlined': {
    fontWeight: 200,
  },
}));

const StyledMenuItem = styled(MenuItem)({
  textDecoration: 'none',
  '&.active': {
    '& .MuiMenuItem-root': {
      '& .MuiTypography-root': {
        fontWeight: 700,
      },
      '& .material-symbols-outlined': {
        fontVariationSettings: '"FILL" 1, "wght" 300, "GRAD" 0, "opsz" 48',
      },
    },
  },
});

type NavItemProps = {
  testId?: string;
  to: string;
  onClick?: () => void;
  itemText: string;
  icon: ReactNode;
  children?: ReactNode | ReactNode[];
};

const NavItem = ({ to, onClick, itemText, icon, testId, children }: NavItemProps) => {
  const navigate = useNavigate();
  return (
    <StyledMenuItem
      farmlands-variant='dark'
      data-testid={testId}
      role='menuitem'
      onClick={() => {
        if (onClick) {
          onClick();
        } else {
          if (to === '/products')
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Products, position: 0 });
          else if (to === '/orders')
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Orders, position: 0 });
          else if (to === '/agreements')
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Agreements, position: 0 });
          navigate(to);
        }
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText>{itemText}</ListItemText>
      {children}
    </StyledMenuItem>
  );
};

const Nav = () => {
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const route = useLocation();
  const navigate = useNavigate();
  const remoteConfig = useSelector(selectRemoteConfig);
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const hasAccess = currentAccount?.hasShareholderAccess || currentAccount?.hasTfoAccess;

  const [value, setValue] = useState(route.pathname);

  useEffect(() => {
    setValue(route.pathname);
  }, [route]);

  const appInstalled =
    'standalone' in window.navigator && window.matchMedia('(display-mode: standalone)').matches;

  const getIconVariant = (route: string): 'fill' | 'outlined' =>
    window.location.pathname.includes(route) ? 'fill' : 'outlined';

  const sideNavItems = useMemo(() => {
    if (hasAccess) {
      return (
        <MenuList tabIndex={0} role='menu'>
          <NavItem
            key='/side-bar-home'
            to={HOME_ROUTE}
            itemText='Home'
            icon={<MaterialSymbol variant={getIconVariant(HOME_ROUTE)}>Home</MaterialSymbol>}
            testId={TestIds.components.sideDrawer.home}
          />
          <NavItem
            key='/side-bar-agreements'
            to={AGREEMENTS_ROUTE}
            itemText='Agreements'
            icon={<MaterialSymbol variant={getIconVariant(AGREEMENTS_ROUTE)}>Note</MaterialSymbol>}
            testId={TestIds.components.sideDrawer.agreements}
          />
          <NavItem
            key='/side-bar-products'
            to={PRODUCTS_ROUTE}
            itemText={'Shop'}
            icon={
              <MaterialSymbol variant={getIconVariant(PRODUCTS_ROUTE)}>
                shopping_cart
              </MaterialSymbol>
            }
            testId={TestIds.components.sideDrawer.pastProducts}
          />
          {remoteConfig.seedEnabled && (
            <NavItem
              key='/side-bar-seed'
              to={SEED_ROUTE}
              itemText={'Seed'}
              icon={
                <MaterialSymbol variant={getIconVariant(SEED_ROUTE)}>Psychiatry</MaterialSymbol>
              }
              testId={TestIds.components.sideDrawer.seed}
            />
          )}

          {remoteConfig.quoteEnabled && (
            <NavItem
              key='/side-bar-quotes'
              to={QUOTES_ROUTE}
              itemText='Quotes'
              icon={
                <MaterialSymbol variant={getIconVariant(QUOTES_ROUTE)}>
                  request_quote
                </MaterialSymbol>
              }
              testId={TestIds.components.sideDrawer.quotes}
            />
          )}
          <NavItem
            key='/side-bar-orders'
            to={ORDERS_ROUTE}
            itemText='Orders'
            icon={<MaterialSymbol variant={getIconVariant(ORDERS_ROUTE)}>contract</MaterialSymbol>}
            testId={TestIds.components.sideDrawer.orders}
          />
        </MenuList>
      );
    } else {
      return (
        <MenuList tabIndex={0} role='menu'>
          <NavItem
            key='/side-bar-home'
            to={HOME_ROUTE}
            itemText='Home'
            icon={<MaterialSymbol variant='fill'>Home</MaterialSymbol>}
            testId={TestIds.components.sideDrawer.home}
          />
        </MenuList>
      );
    }
  }, [hasAccess, window.location.pathname, remoteConfig]);

  const bottomNavItems = useMemo(() => {
    if (hasAccess) {
      return [
        <BottomNavigationAction
          label='Home'
          key={HOME_ROUTE_NAME}
          value={HOME_ROUTE}
          onClick={() => navigate(HOME_ROUTE)}
          icon={<MaterialSymbol variant={getIconVariant(HOME_ROUTE)}>Home</MaterialSymbol>}
          data-testid={TestIds.components.bottomDrawer.home}
        />,
        <BottomNavigationAction
          label='Shop'
          key={PRODUCTS_ROUTE_NAME}
          value={PRODUCTS_ROUTE}
          onClick={() => {
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Products, position: 0 });
            navigate('/products');
          }}
          icon={
            <MaterialSymbol variant={getIconVariant(PRODUCTS_ROUTE)}>shopping_cart</MaterialSymbol>
          }
          data-testid={TestIds.components.bottomDrawer.pastProducts}
        />,
        remoteConfig.seedEnabled ? (
          <BottomNavigationAction
            label='Seed'
            key={SEED_ROUTE_NAME}
            value={SEED_ROUTE}
            onClick={() => {
              saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Seeds, position: 0 });
              navigate(SEED_ROUTE);
            }}
            icon={<MaterialSymbol variant={getIconVariant(SEED_ROUTE)}>Psychiatry</MaterialSymbol>}
            data-testid={TestIds.components.bottomDrawer.seed}
          />
        ) : null,
        <BottomNavigationAction
          label='Orders'
          key={ORDERS_ROUTE_NAME}
          value={ORDERS_ROUTE}
          onClick={() => {
            saveScrollYPosition({ sessionStorageKey: ScrollYPositionKeys.Orders, position: 0 });
            navigate(ORDERS_ROUTE);
          }}
          icon={<MaterialSymbol variant={getIconVariant(ORDERS_ROUTE)}>contract</MaterialSymbol>}
          data-testid={TestIds.components.bottomDrawer.orders}
        />,

        <BottomNavigationAction
          label='Account'
          key={ACCOUNT_ROUTE_NAME}
          value={ACCOUNT_ROUTE}
          onClick={() => {
            saveScrollYPosition({
              sessionStorageKey: ScrollYPositionKeys.Orders,
              position: 0,
            });
            navigate(ACCOUNT_ROUTE);
          }}
          icon={<MaterialSymbol variant={getIconVariant(ACCOUNT_ROUTE)}>Person</MaterialSymbol>}
          data-testid={TestIds.components.bottomDrawer.account}
        />,
      ];
    } else {
      return [
        <BottomNavigationAction
          label='Home'
          key={HOME_ROUTE_NAME}
          value={HOME_ROUTE}
          onClick={() => navigate('/')}
          icon={<MaterialSymbol variant={getIconVariant(HOME_ROUTE)}>Home</MaterialSymbol>}
          data-testid={TestIds.components.bottomDrawer.home}
        />,

        <BottomNavigationAction
          label='Account'
          key={ACCOUNT_ROUTE_NAME}
          value={ACCOUNT_ROUTE}
          onClick={() => navigate(ACCOUNT_ROUTE)}
          icon={<MaterialSymbol variant={getIconVariant(ACCOUNT_ROUTE)}>Person</MaterialSymbol>}
          data-testid={TestIds.components.bottomDrawer.account}
        />,
      ];
    }
  }, [hasAccess, window.location.pathname]);

  return (
    <>
      <MuiDrawer
        role='navigation'
        farmlands-variant='desktop'
        variant='persistent'
        open={!isMedium}
        data-testid={TestIds.components.layout.nav.desktopDrawer}
      >
        <DrawerContainer>
          <LogoWrapper onClick={Capacitor.DEBUG ? () => onClickDeveloper() : undefined}>
            <Logo src={logo} alt='Farmlands logo' />
          </LogoWrapper>
          {sideNavItems}
        </DrawerContainer>
      </MuiDrawer>

      <BottomNavigation
        showLabels
        value={value}
        appInstalled={appInstalled}
        data-testid={TestIds.components.layout.nav.bottomNavigation}
      >
        {[...bottomNavItems]}
      </BottomNavigation>
    </>
  );
};

export default Nav;
